import React, { useState, useEffect, useContext } from "react";
import { StepContext } from "../../../Context/StepContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import { 
  Table, 
  Card, 
  Container, 
  Row, 
  Col, 
  InputGroup, 
  FormControl, 
  Badge, 
  Spinner,
  Pagination,
  Button,
  Dropdown
} from "react-bootstrap";
import { 
  FaSearch, 
  FaShoppingCart, 
  FaExclamationTriangle, 
  FaCar, 
  FaUser, 
  FaCalendarAlt, 
  FaMapMarkerAlt,
  FaEye,
  FaDownload,
  FaTrash
} from "react-icons/fa";

const CartData = () => {
  const { cart } = useContext(StepContext);
  const [filteredCart, setFilteredCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const pageLimit = 5; // Maximum number of pagination items to display at once

  useEffect(() => {
    // Simulate loading effect
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Filter cart based on search term
    if (searchTerm) {
      const filtered = cart.filter(item => 
        (item.fname && item.fname.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.lname && item.lname.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.phone && item.phone.includes(searchTerm)) ||
        (item.carNumber && item.carNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.postcode && item.postcode.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.Model && item.Model.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCart(filtered);
      setCurrentPage(1);
    } else {
      setFilteredCart(cart);
    }
  }, [searchTerm, cart]);

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCart.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCart.length / itemsPerPage);

  // Function to render pagination items
  const renderPaginationItems = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    let endPage = startPage + pageLimit - 1;
    
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - pageLimit + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  // Function to format date for better display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      // Attempt to format using Date if it's a valid date string
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      // If not a valid date, return as is
      return dateString;
    } catch (e) {
      return dateString;
    }
  };

  // Export cart data functionality (placeholder)
  const handleExportData = () => {
    toast.info("Export functionality would be implemented here");
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 font-weight-bold text-primary">
                    <FaShoppingCart className="me-2" /> Active Cart Data
                  </h5>
                  {loading ? (
                    <Spinner animation="border" size="sm" className="ms-3" />
                  ) : (
                    <Badge bg="info" className="ms-2">
                      {filteredCart.length}
                    </Badge>
                  )}
                </div>
                
                <div className="d-flex">
                  <InputGroup className="me-3" style={{ width: "250px" }}>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search carts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                  
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-primary" id="dropdown-actions">
                      Actions
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleExportData}>
                        <FaDownload className="me-2" /> Export Data
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading cart data...</p>
                  </div>
                ) : filteredCart.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaCalendarAlt className="me-2 text-muted" />
                              Booking Date
                            </div>
                          </th>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaUser className="me-2 text-muted" />
                              Customer
                            </div>
                          </th>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaMapMarkerAlt className="me-2 text-muted" />
                              Postcode
                            </div>
                          </th>
                          <th className="py-3">
                            <div className="d-flex align-items-center">
                              <FaCar className="me-2 text-muted" />
                              Vehicle Details
                            </div>
                          </th>
                          <th className="py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((item, index) => (
                          <tr key={index}>
                            <td className="py-3">
                              {formatDate(item.bookingDate) || "N/A"}
                            </td>
                            <td className="py-3">
                              <div className="d-flex flex-column">
                                <span className="fw-bold">
                                  {item.fname} {item.lname}
                                </span>
                                <small className="text-muted">{item.phone || "No phone"}</small>
                              </div>
                            </td>
                            <td className="py-3">
                              {item.postcode || "N/A"}
                            </td>
                            <td className="py-3">
                              <div className="d-flex flex-column">
                                <span>{item.Model || "N/A"}</span>
                                <small className="text-muted">
                                  Car #: {item.carNumber || "N/A"} | 
                                  Reg: {formatDate(item.firstRegistrationDate) || "N/A"} | 
                                  MOT: {formatDate(item.motExpiryDate) || "N/A"}
                                </small>
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="d-flex justify-content-center">
                                <Button 
                                  variant="outline-info" 
                                  size="sm" 
                                  className="me-2"
                                  title="View Cart Details"
                                  onClick={() => toast.info(`View details for cart: ${item.fname} ${item.lname}`)}
                                >
                                  <FaEye />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  title="Remove Cart"
                                  onClick={() => toast.info(`Remove cart functionality would be implemented here`)}
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-5 empty-state">
                    <FaExclamationTriangle size={48} className="text-muted mb-3" />
                    <h5>No cart data found</h5>
                    <p className="text-muted">
                      {searchTerm ? "No carts match your search criteria." : "There are no active carts at this time."}
                    </p>
                    {searchTerm && (
                      <Button variant="outline-primary" onClick={() => setSearchTerm("")}>
                        Clear Search
                      </Button>
                    )}
                  </div>
                )}
              </Card.Body>
              
              {filteredCart.length > 0 && (
                <Card.Footer className="bg-white border-0 px-4 py-3">
                  <Row className="align-items-center">
                    <Col>
                      <small className="text-muted">
                        Showing {filteredCart.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, filteredCart.length)} of {filteredCart.length} carts
                      </small>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      {totalPages > 1 && (
                        <Pagination className="mb-0">
                          <Pagination.First
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          {renderPaginationItems()}
                          <Pagination.Next
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      )}
                    </Col>
                  </Row>
                </Card.Footer>
              )}
            </Card>
          </Container>
        </main>
      </div>
    </div>
  );
};

export default CartData;